import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';

// ============================================================================
// Default Fallback Component
// ============================================================================

function DefaultPlaceholder({ aspectRatio }: { aspectRatio: string }) {
  return (
    <div className={`relative ${aspectRatio} bg-stone/20 rounded-4xl flex flex-col items-center justify-center border border-stone`}>
      <ImageOff className="w-12 h-12 text-sand/50 mb-3" />
      <div className="text-sand text-sm font-light">Image not available</div>
      <div className="text-sand/60 text-xs mt-1">Coming soon</div>
    </div>
  );
}

// ============================================================================
// Types
// ============================================================================

export interface ComparisonSliderProps {
  /** Source URL for the "before" image */
  beforeSrc: string;
  /** Source URL for the "after" image */
  afterSrc: string;
  /** Alt text for the before image */
  beforeAlt?: string;
  /** Alt text for the after image */
  afterAlt?: string;
  /** Label displayed on the before side */
  beforeLabel?: string;
  /** Label displayed on the after side */
  afterLabel?: string;
  /** Whether to show the before/after labels */
  showLabels?: boolean;
  /** Tailwind aspect ratio class (e.g., "aspect-[4/3]", "aspect-[16/9]") */
  aspectRatio?: string;
  /** Initial slider position (0-100) */
  initialPosition?: number;
  /** Whether to animate the slider when user hasn't interacted */
  enableAutoDemo?: boolean;
  /** Auto demo interval in milliseconds */
  autoDemoInterval?: number;
  /** Additional CSS classes for the container */
  className?: string;
  /** Callback when user interacts with the slider */
  onInteraction?: () => void;
  /** Callback when image fails to load */
  onImageError?: (type: 'before' | 'after', error: Error) => void;
  /** Placeholder component to show while loading or on error */
  placeholder?: React.ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export function ComparisonSlider({
  beforeSrc,
  afterSrc,
  beforeAlt = 'Before',
  afterAlt = 'After',
  beforeLabel = 'Before',
  afterLabel = 'After',
  showLabels = true,
  aspectRatio = 'aspect-[4/3]',
  initialPosition = 50,
  enableAutoDemo = true,
  autoDemoInterval = 3000,
  className = '',
  onInteraction,
  onImageError,
  placeholder,
}: ComparisonSliderProps) {
  // State - only used for React-controlled updates (auto-demo, keyboard, accessibility)
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [beforeLoaded, setBeforeLoaded] = useState(false);
  const [afterLoaded, setAfterLoaded] = useState(false);
  const [beforeError, setBeforeError] = useState(false);
  const [afterError, setAfterError] = useState(false);

  // Refs for direct DOM manipulation (bypasses React for snappy performance)
  const containerRef = useRef<HTMLDivElement>(null);
  const afterContainerRef = useRef<HTMLDivElement>(null);
  const sliderHandleRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const beforeLabelRef = useRef<HTMLDivElement>(null);
  const afterLabelRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef(initialPosition);
  const hasInteractedRef = useRef(false);

  // Sync state position to DOM on mount and when position state changes (for auto-demo/keyboard)
  useEffect(() => {
    positionRef.current = position;
    updateDOMPosition(position);
  }, [position]);

  // Direct DOM update function - no React re-renders
  const updateDOMPosition = useCallback((percentage: number) => {
    if (afterContainerRef.current) {
      afterContainerRef.current.style.clipPath = `inset(0 ${100 - percentage}% 0 0)`;
    }
    if (sliderHandleRef.current) {
      sliderHandleRef.current.style.left = `${percentage}%`;
    }
    if (glowRef.current) {
      glowRef.current.style.left = `${percentage}%`;
    }
    // Update label opacity based on position
    // afterLabel is on the left (over the clipped after image), hide when slider near left edge
    if (afterLabelRef.current) {
      afterLabelRef.current.style.opacity = percentage < 20 ? '0' : '1';
    }
    // beforeLabel is on the right (over the before image background), hide when slider near right edge
    if (beforeLabelRef.current) {
      beforeLabelRef.current.style.opacity = percentage > 80 ? '0' : '1';
    }
  }, []);

  // Handle position calculation from client X - direct DOM manipulation for instant response
  const handleMove = useCallback((clientX: number) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    
    // Update ref and DOM directly - no React state update during drag
    positionRef.current = percentage;
    updateDOMPosition(percentage);
    
    if (!hasInteractedRef.current) {
      hasInteractedRef.current = true;
      setHasInteracted(true);
      onInteraction?.();
    }
  }, [onInteraction, updateDOMPosition]);

  // Sync position ref to state on drag end (for accessibility aria-valuenow)
  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    // Sync the final position to React state for accessibility
    setPosition(positionRef.current);
  }, []);

  // Mouse events
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    handleMove(e.clientX);
  }, [handleMove]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return;
    handleMove(e.clientX);
  }, [isDragging, handleMove]);

  // Touch events
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setIsDragging(true);
    handleMove(e.touches[0].clientX);
  }, [handleMove]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    handleMove(e.touches[0].clientX);
  }, [handleMove]);

  // Keyboard navigation for accessibility
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 2;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setPosition(prev => {
        const newPos = Math.max(0, prev - step);
        positionRef.current = newPos;
        return newPos;
      });
      if (!hasInteractedRef.current) {
        hasInteractedRef.current = true;
        setHasInteracted(true);
        onInteraction?.();
      }
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setPosition(prev => {
        const newPos = Math.min(100, prev + step);
        positionRef.current = newPos;
        return newPos;
      });
      if (!hasInteractedRef.current) {
        hasInteractedRef.current = true;
        setHasInteracted(true);
        onInteraction?.();
      }
    }
  }, [onInteraction]);

  // Global mouse/touch up handlers
  useEffect(() => {
    const handleGlobalMouseUp = () => handleDragEnd();
    const handleGlobalTouchEnd = () => handleDragEnd();
    
    // Also listen for mousemove globally when dragging for smoother tracking
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      handleMove(e.clientX);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('touchend', handleGlobalTouchEnd, { passive: true });
    
    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [isDragging, handleMove, handleDragEnd]);

  // Auto-demo animation when not interacted
  useEffect(() => {
    if (hasInteracted || !enableAutoDemo) return;

    // Check for reduced motion preference
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const demoAnimation = setInterval(() => {
      setPosition(prev => (prev > 60 ? 40 : 60));
    }, autoDemoInterval);

    return () => clearInterval(demoAnimation);
  }, [hasInteracted, enableAutoDemo, autoDemoInterval]);

  // Image load/error handlers
  const handleBeforeLoad = () => setBeforeLoaded(true);
  const handleAfterLoad = () => setAfterLoaded(true);
  
  const handleBeforeError = () => {
    setBeforeError(true);
    onImageError?.('before', new Error(`Failed to load before image: ${beforeSrc}`));
  };
  
  const handleAfterError = () => {
    setAfterError(true);
    onImageError?.('after', new Error(`Failed to load after image: ${afterSrc}`));
  };

  const isLoading = !beforeLoaded || !afterLoaded;
  const hasError = beforeError || afterError;

  // Show placeholder if there's an error
  if (hasError) {
    return placeholder ? <>{placeholder}</> : <DefaultPlaceholder aspectRatio={aspectRatio} />;
  }

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-4xl shadow-soft-xl cursor-ew-resize select-none ${aspectRatio} ${className}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="slider"
      aria-label="Image comparison slider"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(position)}
      aria-valuetext={`Showing ${Math.round(position)}% of the after image`}
    >
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-stone/20 animate-pulse z-10 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-olive/30 border-t-olive rounded-full animate-spin" />
        </div>
      )}

      {/* Before Image (Empty Room) - Full width background */}
      <img
        src={beforeSrc}
        alt={beforeAlt}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
        onLoad={handleBeforeLoad}
        onError={handleBeforeError}
      />

      {/* After Image (Staged) - GPU-accelerated clip-path */}
      <div
        ref={afterContainerRef}
        className="absolute inset-0"
        style={{ 
          clipPath: `inset(0 ${100 - position}% 0 0)`,
          willChange: 'clip-path'
        }}
      >
        <img
          src={afterSrc}
          alt={afterAlt}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
          onLoad={handleAfterLoad}
          onError={handleAfterError}
        />
      </div>

      {/* Glow line effect on drag */}
      <div
        ref={glowRef}
        className="absolute top-0 bottom-0 w-8 pointer-events-none transition-opacity duration-300"
        style={{
          left: `${position}%`,
          transform: 'translateX(-50%)',
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
          opacity: isDragging ? 1 : 0,
        }}
      />

      {/* Labels with fade animation based on position */}
      {showLabels && (
        <>
          <div
            ref={afterLabelRef}
            className="absolute top-4 left-4 bg-olive/90 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-light transition-opacity duration-300"
            style={{ opacity: position < 20 ? 0 : 1 }}
          >
            {afterLabel}
          </div>
          <div
            ref={beforeLabelRef}
            className="absolute top-4 right-4 bg-charcoal/70 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-light transition-opacity duration-300"
            style={{ opacity: position > 80 ? 0 : 1 }}
          >
            {beforeLabel}
          </div>
        </>
      )}

      {/* Slider Handle - GPU-accelerated positioning */}
      <div
        ref={sliderHandleRef}
        className="absolute top-0 bottom-0 w-1 bg-white/90 shadow-2xl"
        style={{
          left: `${position}%`,
          transform: 'translateX(-50%)',
          boxShadow: isDragging 
            ? '0 0 20px rgba(255,255,255,0.5)' 
            : '0 0 10px rgba(0,0,0,0.3)',
          willChange: 'left'
        }}
      >
        {/* Handle circle - responsive size */}
        <div
          className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 
            w-10 h-10 sm:w-12 sm:h-12 
            bg-white rounded-full shadow-lift flex items-center justify-center 
            transition-transform duration-150 ${isDragging ? 'scale-110' : ''}`}
          style={{ 
            boxShadow: isDragging 
              ? '0 8px 30px rgba(0,0,0,0.2)' 
              : '0 4px 15px rgba(0,0,0,0.15)'
          }}
        >
          <ChevronLeft 
            className={`w-3 h-3 sm:w-4 sm:h-4 text-charcoal transition-transform duration-150 ${
              isDragging ? '-translate-x-0.5' : ''
            }`} 
          />
          <ChevronRight 
            className={`w-3 h-3 sm:w-4 sm:h-4 text-charcoal transition-transform duration-150 ${
              isDragging ? 'translate-x-0.5' : ''
            }`} 
          />
        </div>
      </div>

      {/* Instruction hint - fades after interaction */}
      <div
        className={`absolute bottom-4 left-1/2 -translate-x-1/2 bg-charcoal/60 backdrop-blur-sm text-white/90 px-4 py-2 rounded-full text-xs font-light transition-all duration-500 ${
          hasInteracted ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
      >
        ← Drag to compare →
      </div>

      {/* Subtle border */}
      <div className="absolute inset-0 border border-white/10 rounded-4xl pointer-events-none" />
    </div>
  );
}

export default ComparisonSlider;
